/**
 * @fileoverview Pure SpreadsheetSpec → array-of-arrays / CSV / xlsx workbook.
 * Pure functions are unit-tested; `buildWorkbook` wraps xlsx for download.
 */
import * as XLSX from "xlsx";
import { AI_DISCLAIMER } from "@cvx/lib/aiDisclaimer";
import type { SpreadsheetSpec } from "@cvx/lib/spreadsheets/spec";

type Sheet = SpreadsheetSpec["sheets"][number];
type Cell = string | number;

/** Header row of labels + one array per row, values in column order. */
export function specToAoa(sheet: Sheet): Cell[][] {
  const header = sheet.columns.map((c) => c.label);
  const body = sheet.rows.map((row) =>
    sheet.columns.map((c) => {
      const v = row[c.key];
      return v === null || v === undefined ? "" : v;
    }),
  );
  return [header, ...body];
}

function csvCell(v: Cell): string {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** CSV of the first sheet (CSV is single-sheet by nature). */
export function firstSheetToCsv(spec: SpreadsheetSpec): string {
  return specToAoa(spec.sheets[0])
    .map((row) => row.map(csvCell).join(","))
    .join("\n");
}

/**
 * Multi-sheet xlsx workbook.
 *
 * The AI disclaimer is prepended here rather than inside `specToAoa`, because
 * `specToAoa` also feeds `firstSheetToCsv` and CSV must stay machine-parseable.
 */
export function buildWorkbook(spec: SpreadsheetSpec): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  for (const sheet of spec.sheets) {
    const ws = XLSX.utils.aoa_to_sheet([
      [AI_DISCLAIMER],
      [],
      ...specToAoa(sheet),
    ]);
    XLSX.utils.book_append_sheet(wb, ws, sheet.name.slice(0, 31));
  }
  return wb;
}
